import React, { useRef, useState } from "react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

async function grabVideoPoster(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata"; video.muted = true; video.playsInline = true;
    video.src = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(video.src);
    const onLoaded = () => { try { video.currentTime = Math.min(0.1, (video.duration || 1) / 10); } catch { resolve(null); cleanup(); } };
    const onSeeked = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(b => { cleanup(); resolve(b); }, "image/jpeg", 0.85);
      } catch { cleanup(); resolve(null); }
    };
    video.addEventListener("loadedmetadata", onLoaded, { once: true });
    video.addEventListener("seeked", onSeeked, { once: true });
    video.addEventListener("error", () => { cleanup(); resolve(null); }, { once: true });
    setTimeout(() => { cleanup(); resolve(null); }, 8000);
  });
}

/**
 * Upload for images AND videos. Auto-detects type by file MIME and renders the correct preview.
 * Used for gallery items so managers can drop a mp4/mov straight in.
 */
export default function MediaUpload({ value, onChange, onPoster, label = "Image or video URL", testId, accept = "image/*,video/*" }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const ref = useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url;
      onChange(url);
      // Video? Auto-generate a poster from the first frame (client-side canvas capture)
      if (data.media_type === "video" && typeof onPoster === "function") {
        try {
          const posterBlob = await grabVideoPoster(file);
          if (posterBlob) {
            const pf = new FormData(); pf.append("file", new File([posterBlob], "poster.jpg", { type: "image/jpeg" }));
            const pr = await api.post("/admin/upload", pf, { headers: { "Content-Type": "multipart/form-data" } });
            onPoster(pr.data.url);
            toast.success("Video + auto-poster uploaded");
          } else {
            toast.success("Video uploaded");
          }
        } catch {
          toast.success("Video uploaded (poster auto-grab failed — you can set one manually)");
        }
      } else {
        toast.success(`${data.media_type === "video" ? "Video" : "Image"} uploaded`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const isVideo = typeof value === "string" && /\.(mp4|webm|mov|quicktime)(\?|$)/i.test(value);

  return (
    <div>
      <label className="eyebrow block mb-1">{label}</label>
      <div className="flex gap-2 items-start">
        <input type="text" value={value || ""} onChange={e => onChange(e.target.value)}
          placeholder="Paste URL or drop image/video →"
          className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" />
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
          onClick={() => ref.current?.click()}
          className={`w-24 h-10 border ${dragging ? "border-red-600 bg-red-600/10" : "border-white/10"} grid place-items-center cursor-pointer hover:border-red-600`}
          data-testid={testId ? `${testId}-upload` : "media-upload"}
        >
          {uploading ? <Loader2 className="animate-spin" size={14}/> : <div className="flex items-center gap-1 text-xs text-zinc-400"><Upload size={12}/>Upload</div>}
        </div>
        <input type="file" accept={accept} ref={ref} className="hidden" onChange={e => upload(e.target.files?.[0])} />
      </div>
      {value && typeof value === "string" && value.startsWith("http") && (
        isVideo ? (
          <video src={value} className="mt-2 h-20 object-cover border border-white/10" muted playsInline controls={false} />
        ) : (
          <img src={value} alt="" className="mt-2 h-20 object-cover border border-white/10" />
        )
      )}
    </div>
  );
}
